'use client'

import { type TrialLevel, trialLevelSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import React from 'react'

import {
  BLevelIcon,
  C1LevelIcon,
  C2LevelIcon,
  DLevelIcon,
  PrimaryBLevelIcon,
  PrimaryC1LevelIcon,
  PrimaryC2LevelIcon,
  PrimaryDLevelIcon,
} from '@podo/assets'

export type LevelType = { name: string; description: string; Icon: React.ReactNode; level: TrialLevel } & (
  | { default: true; defaultMessage: string }
  | { default: false }
)

export type LevelTypeByLanguage = {
  EN: Record<TrialLevel, LevelType>
  JP: Record<TrialLevel, LevelType>
}

export const useTrialSubscribeStaticModel = (isPrimary = false) => {
  const t = useTranslation()

  const LANG_TYPE_MAP = {
    EN: t('subscribeList.trialStatic.language.en'),
    JP: t('subscribeList.trialStatic.language.ja'),
  } as const

  const LEVEL_TYPE_BY_LANGUAGE: LevelTypeByLanguage = {
    EN: {
      B: {
        default: false,
        name: t('subscribeList.trialStatic.enLevels.b.name'),
        description: t('subscribeList.trialStatic.enLevels.b.description'),
        level: trialLevelSchema.Enum.B,
        Icon: isPrimary ? <PrimaryBLevelIcon /> : <BLevelIcon />,
      },
      C1: {
        default: true,
        name: t('subscribeList.trialStatic.enLevels.c1.name'),
        defaultMessage: t('subscribeList.trialStatic.enLevels.c1.defaultMessage'),
        description: t('subscribeList.trialStatic.enLevels.c1.description'),
        level: trialLevelSchema.Enum.C1,
        Icon: isPrimary ? <PrimaryC1LevelIcon /> : <C1LevelIcon />,
      },
      C2: {
        default: false,
        name: t('subscribeList.trialStatic.enLevels.c2.name'),
        description: t('subscribeList.trialStatic.enLevels.c2.description'),
        level: trialLevelSchema.Enum.C2,
        Icon: isPrimary ? <PrimaryC2LevelIcon /> : <C2LevelIcon />,
      },
      D: {
        default: false,
        name: t('subscribeList.trialStatic.enLevels.d.name'),
        description: t('subscribeList.trialStatic.enLevels.d.description'),
        level: trialLevelSchema.Enum.D,
        Icon: isPrimary ? <PrimaryDLevelIcon /> : <DLevelIcon />,
      },
    },
    JP: {
      B: {
        default: true,
        name: t('subscribeList.trialStatic.jaLevels.b.name'),
        description: t('subscribeList.trialStatic.jaLevels.b.description'),
        defaultMessage: t('subscribeList.trialStatic.jaLevels.b.defaultMessage'),
        level: trialLevelSchema.Enum.B,
        Icon: isPrimary ? <PrimaryBLevelIcon /> : <BLevelIcon />,
      },
      C1: {
        default: false,
        name: t('subscribeList.trialStatic.jaLevels.c1.name'),
        description: t('subscribeList.trialStatic.jaLevels.c1.description'),
        level: trialLevelSchema.Enum.C1,
        Icon: isPrimary ? <PrimaryC1LevelIcon /> : <C1LevelIcon />,
      },
      C2: {
        default: false,
        name: t('subscribeList.trialStatic.jaLevels.c2.name'),
        description: t('subscribeList.trialStatic.jaLevels.c2.description'),
        level: trialLevelSchema.Enum.C2,
        Icon: isPrimary ? <PrimaryC2LevelIcon /> : <C2LevelIcon />,
      },
      D: {
        default: false,
        name: t('subscribeList.trialStatic.jaLevels.d.name'),
        description: t('subscribeList.trialStatic.jaLevels.d.description'),
        level: trialLevelSchema.Enum.D,
        Icon: isPrimary ? <PrimaryDLevelIcon /> : <DLevelIcon />,
      },
    },
  } as const

  return {
    LANG_TYPE_MAP,
    LEVEL_TYPE_BY_LANGUAGE,
  }
}
